import { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { auth, db } from '../../firebase/config';
import { doc, updateDoc, getDoc, increment, collection, query, where, getDocs } from 'firebase/firestore';
import { useUser } from '../../context/UserContext';

// Constants and interfaces
interface Position {
  x: number;
  y: number;
}

interface UserStats {
  tokens: number;
  gamesPlayed: number;
  highScores: {
    [key: string]: number;
  };
  displayName?: string;
}

interface GameConfig {
  id: string;
  title: string;
  description: string;
  url: string;
  imageUrl: string;
  isActive: boolean;
  addedDate: Date;
  category: string;
  requiredTokens: number;
}

const GRID_SIZE = 20;
const CELL_SIZE = 20;
const INITIAL_SNAKE = [{ x: 10, y: 10 }];
const INITIAL_DIRECTION = { x: 1, y: 0 };
const INITIAL_FOOD = { x: 15, y: 15 };

const MOBILE_CONTROLS = [
  { key: 'ArrowUp', label: '↑' },
  { key: 'ArrowLeft', label: '←' },
  { key: 'ArrowRight', label: '→' },
  { key: 'ArrowDown', label: '↓' },
];

const Snake = () => {
  const { userName, stats, refreshUserData } = useUser();
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [direction, setDirection] = useState(INITIAL_DIRECTION);
  const [food, setFood] = useState(INITIAL_FOOD);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [highScore, setHighScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(true);
  const [foodEaten, setFoodEaten] = useState(false);
  const [gameConfig, setGameConfig] = useState<GameConfig | null>(null);

  // Move snake - define this first
  const moveSnake = () => {
    const newSnake = [...snake];
    const head = {
      x: newSnake[0].x + direction.x,
      y: newSnake[0].y + direction.y
    };

    if (checkCollision(head)) {
      setGameOver(true);
      return;
    }

    newSnake.unshift(head);

    if (head.x === food.x && head.y === food.y) {
      setScore(s => s + 10);
      setFoodEaten(true);
      setTimeout(() => setFoodEaten(false), 200);
      generateFood();
    } else {
      newSnake.pop();
    }

    setSnake(newSnake);
  };

  // Game loop - now moveSnake is defined before being used
  useEffect(() => {
    if (gameOver || isPaused) return;

    const interval = setInterval(() => {
      moveSnake();
    }, 150);

    return () => clearInterval(interval);
  }, [snake, direction, gameOver, isPaused]);

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
          if (direction.y === 0) setDirection({ x: 0, y: -1 });
          break;
        case 'ArrowDown':
          if (direction.y === 0) setDirection({ x: 0, y: 1 });
          break;
        case 'ArrowLeft':
          if (direction.x === 0) setDirection({ x: -1, y: 0 });
          break;
        case 'ArrowRight':
          if (direction.x === 0) setDirection({ x: 1, y: 0 });
          break;
        case ' ':
          setIsPaused(p => !p);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [direction]);

  // Generate new food position
  const generateFood = useCallback(() => {
    let newFood: Position;
    do {
      newFood = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE)
      };
    } while (snake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    setFood(newFood);
  }, [snake]);

  // Check collisions
  const checkCollision = useCallback((head: { x: number; y: number }) => {
    // Wall collision
    if (
      head.x < 0 || 
      head.x >= GRID_SIZE || 
      head.y < 0 || 
      head.y >= GRID_SIZE
    ) {
      return true;
    }

    // Self collision
    return snake.some(segment => segment.x === head.x && segment.y === head.y);
  }, [snake]);

  // Fetch high score
  useEffect(() => {
    const fetchHighScore = async () => {
      if (!auth.currentUser) return;
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const docSnap = await getDoc(userRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setHighScore(data.highScores?.snake || 0);
      }
    };
    fetchHighScore();
  }, []);

  // Update high score and tokens
  const updateUserStats = async () => {
    if (!auth.currentUser) return;
    const userRef = doc(db, 'users', auth.currentUser.uid);
    
    // Update local high score first
    const newHighScore = Math.max(score, highScore);
    setHighScore(newHighScore);

    try {
      await updateDoc(userRef, {
        'highScores.snake': newHighScore,
        tokens: increment(Math.floor(score / 10)),
        gamesPlayed: increment(1)
      });
      
      // Add this line to refresh the user context
      await refreshUserData();  // Make sure this is called
    } catch (error) {
      console.error('Error updating stats:', error);
    }
  };

  // Modified game over handling
  useEffect(() => {
    if (gameOver) {
      updateUserStats();
    }
  }, [gameOver, score, highScore]);

  // Reset game
  const resetGame = () => {
    setSnake(INITIAL_SNAKE);
    setDirection(INITIAL_DIRECTION);
    setFood(INITIAL_FOOD);
    setGameOver(false);
    setScore(0);
    setIsPaused(false);
  };

  // Add this useEffect to fetch game config
  useEffect(() => {
    const fetchGameConfig = async () => {
      const gamesRef = collection(db, 'games');
      const q = query(gamesRef, where('title', '==', 'Snake'));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        setGameConfig({
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data()
        } as GameConfig);
      }
    };

    fetchGameConfig();
  }, []);

  // Render game board
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Navbar 
        userName={userName} 
        displayName={stats?.displayName}
      />
      <div className="max-w-4xl mx-auto p-8">
        {gameConfig && (
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{gameConfig.title}</h1>
            <p className="text-gray-400">{gameConfig.description}</p>
          </div>
        )}
        <div className="flex flex-col items-center">
          {/* Score and controls */}
          <div className="mb-4 flex justify-between w-full">
            <div>
              <div className="text-2xl font-bold">Score: {score}</div>
              <div className="text-gray-400">High Score: {highScore}</div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => setShowInstructions(true)}
                className="px-4 py-2 bg-indigo-500 rounded hover:bg-indigo-600"
              >
                Help
              </button>
              <button 
                onClick={() => setIsPaused(p => !p)}
                className="px-4 py-2 bg-indigo-500 rounded hover:bg-indigo-600"
              >
                {isPaused ? 'Resume' : 'Pause'}
              </button>
            </div>
          </div>

          {/* Game board */}
          <div 
            className={`relative bg-slate-800 rounded-lg overflow-hidden transition-transform ${foodEaten ? 'scale-105' : ''}`}
            style={{
              width: GRID_SIZE * CELL_SIZE,
              height: GRID_SIZE * CELL_SIZE
            }}
          >
            {/* Render snake */}
            {snake.map((segment, index) => (
              <div
                key={index}
                className="absolute bg-green-500 rounded-sm"
                style={{
                  width: CELL_SIZE - 2,
                  height: CELL_SIZE - 2,
                  left: segment.x * CELL_SIZE,
                  top: segment.y * CELL_SIZE,
                  transform: index === 0 ? 'scale(1.1)' : 'scale(1)',
                }}
              />
            ))}

            {/* Render food */}
            <div
              className="absolute bg-red-500 rounded-full"
              style={{
                width: CELL_SIZE - 2,
                height: CELL_SIZE - 2,
                left: food.x * CELL_SIZE,
                top: food.y * CELL_SIZE
              }}
            />

            {/* Pause overlay */}
            {isPaused && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="text-3xl font-bold">PAUSED</div>
              </div>
            )}
          </div>

          {/* Mobile controls */}
          <div className="mt-8 grid grid-cols-3 gap-4 md:hidden">
            {MOBILE_CONTROLS.map(({ key, label }) => (
              <button
                key={key}
                className={`p-4 text-2xl bg-indigo-500 rounded-full ${
                  key === 'ArrowUp' ? 'col-start-2' :
                  key === 'ArrowDown' ? 'col-start-2' : ''
                }`}
                onClick={() => {
                  const event = new KeyboardEvent('keydown', { key });
                  window.dispatchEvent(event);
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Instructions modal */}
          {showInstructions && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4">
              <div className="bg-slate-800 p-6 rounded-lg max-w-md">
                <h2 className="text-2xl font-bold mb-4">How to Play</h2>
                <ul className="space-y-2 mb-6">
                  <li>• Use arrow keys to control the snake</li>
                  <li>• Collect red food to grow and score points</li>
                  <li>• Avoid walls and your own tail</li>
                  <li>• Space bar to pause/resume</li>
                  <li>• Earn 1 token for every 10 points!</li>
                </ul>
                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full py-2 bg-indigo-500 rounded hover:bg-indigo-600"
                >
                  Start Playing!
                </button>
              </div>
            </div>
          )}

          {/* Game over message */}
          {gameOver && (
            <div className="mt-4 text-center">
              <h2 className="text-2xl font-bold text-red-500 mb-4">Game Over!</h2>
              <div className="mb-4">
                <div>Final Score: {score}</div>
                <div>Tokens Earned: {Math.floor(score / 10)}</div>
              </div>
              <button 
                onClick={resetGame}
                className="px-6 py-3 bg-green-500 rounded-lg hover:bg-green-600"
              >
                Play Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Snake; 